/**
 * Minimal history-API router for CheckTheLineUp.
 *
 * URL shapes:
 *   /                               → events list
 *   /events                         → events list
 *   /events/:slug                   → event → browse
 *   /events/:slug/:tab              → event → tab  (tab ∈ browse|swipe|schedule|stages|clash)
 *   /events/:slug/artist/:artistSlug  → event → browse with artist sheet open
 *   /events/:slug/:tab/artist/:artistSlug  → event → tab with artist sheet open
 *
 * Query string is ignored (kept simple — can add later).
 */
import { useEffect, useState, useCallback } from "react";
import { trackPageView } from "./analytics";

export type Tab = "browse" | "swipe" | "schedule" | "stages" | "clash";

export const ALL_TABS: Tab[] = ["browse", "swipe", "schedule", "stages", "clash"];

export const POLICY_PAGES = ["privacy", "terms", "disclosure", "about", "support"] as const;
export type PolicyPage = typeof POLICY_PAGES[number];

export type Route =
  | { name: "events" }
  | { name: "event"; eventSlug: string; tab: Tab; artistSlug: string | null }
  | { name: "policy"; page: PolicyPage }
  | { name: "share"; token: string };

export function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  // "" or "events"
  if (parts.length === 0) return { name: "events" };
  if ((POLICY_PAGES as readonly string[]).includes(parts[0]!)) {
    return { name: "policy", page: parts[0] as PolicyPage };
  }
  if (parts[0] === "s" && parts[1]) {
    return { name: "share", token: parts[1]! };
  }
  if (parts[0] !== "events") return { name: "events" };
  if (parts.length === 1) return { name: "events" };
  const eventSlug = parts[1]!;

  // /events/:slug
  if (parts.length === 2) {
    return { name: "event", eventSlug, tab: "browse", artistSlug: null };
  }

  // /events/:slug/artist/:artistSlug
  if (parts[2] === "artist" && parts[3]) {
    return { name: "event", eventSlug, tab: "browse", artistSlug: parts[3]! };
  }

  const maybeTab = parts[2];
  const tab = (ALL_TABS as string[]).includes(maybeTab!) ? (maybeTab as Tab) : "browse";

  // /events/:slug/:tab
  if (parts.length === 3) {
    return { name: "event", eventSlug, tab, artistSlug: null };
  }

  // /events/:slug/:tab/artist/:artistSlug
  if (parts[3] === "artist" && parts[4]) {
    return { name: "event", eventSlug, tab, artistSlug: parts[4]! };
  }

  return { name: "event", eventSlug, tab, artistSlug: null };
}

export function routeToUrl(route: Route): string {
  if (route.name === "events") return "/events";
  if (route.name === "policy") return `/${route.page}`;
  if (route.name === "share") return `/s/${route.token}`;
  const base = `/events/${encodeURIComponent(route.eventSlug)}`;
  const tabPart = route.tab === "browse" ? "" : `/${route.tab}`;
  const artistPart = route.artistSlug ? `/artist/${encodeURIComponent(route.artistSlug)}` : "";
  return `${base}${tabPart}${artistPart}` || "/events";
}

/** React hook giving `[route, navigate]`. */
export function useRoute(): [Route, (next: Route, opts?: { replace?: boolean }) => void] {
  const [route, setRoute] = useState<Route>(() =>
    typeof window !== "undefined" ? parseRoute(window.location.pathname) : { name: "events" },
  );

  useEffect(() => {
    const onPop = () => {
      const next = parseRoute(window.location.pathname);
      setRoute(next);
      trackPageView(window.location.pathname);
    };
    window.addEventListener("popstate", onPop);
    // Normalize initial URL — e.g. "/" → "/events" — without triggering pushState.
    if (window.location.pathname === "/" || window.location.pathname === "") {
      history.replaceState({}, "", routeToUrl({ name: "events" }));
    }
    // Fire the initial page_view now that the URL is normalised.
    trackPageView(window.location.pathname);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: Route, opts: { replace?: boolean } = {}) => {
    const url = routeToUrl(next);
    if (window.location.pathname + window.location.search === url) {
      setRoute(next);
      return;
    }
    if (opts.replace) history.replaceState({}, "", url);
    else history.pushState({}, "", url);
    setRoute(next);
    trackPageView(url);
  }, []);

  return [route, navigate];
}
